import Intercom, { show } from '@intercom/messenger-js-sdk';
import cx from 'classnames';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { ComponentProps, memo, useEffect } from 'react';

import useLoggedUser from '../../../hooks/use-logged-user';
import { useAppContext } from '../../providers/app-context';
import Button from '../../ui/button';
import BPImage from '../../ui/v2/image';

const { publicRuntimeConfig } = getConfig();
const APP_ID = publicRuntimeConfig.intercomKey;

function initializeIntercom(user?: ReturnType<typeof useLoggedUser>) {
    if (user) {
        fetch('/api/intercom-hash?userId=' + user.staff.id)
            .then((res) => res.json())
            .then((data) => {
                const { hash } = data;

                Intercom({
                    app_id: APP_ID,
                    custom_launcher_selector: '#intercom-launcher',
                    hide_default_launcher: true,
                    user_id: user.staff.id.toString(),
                    name: user.staff.name,
                    email: user.staff.email,
                    user_hash: hash,
                });
            })
            .catch((err) => {
                console.error('Erro ao obter hash', err);
            });
    } else {
        Intercom({
            app_id: APP_ID,
            custom_launcher_selector: '#intercom-launcher',
            hide_default_launcher: true,
        });
    }
}

const IntercomWrapper = () => {
    const {
        concent: { required },
    } = useAppContext();
    const user = useLoggedUser();

    useEffect(() => {
        if (!window.Intercom) {
            initializeIntercom(user);
        }
    }, [user]);

    if (!required) {
        return 'IC';
    }

    return <IntercomButton />;
};

export default memo(IntercomWrapper);

const IntercomButton = ({ isPlaceholder = false, ...rest }) => {
    const { route } = useRouter();

    return (
        <Button
            {...rest}
            //id="intercom-launcher"
            onClick={() => show()}
            isFullWidth={false}
            isSmall={true}
            className={cx(
                'h-12',
                'w-12',
                'text-center',
                'lg:block',
                'lg:col-span-4',
                'lg:justify-self-end',
                {
                    '!hidden': route.includes('checkout'),
                }
            )}
            disableFocusOnMenu
        >
            <div
                className={cx(
                    'rounded-full',
                    'h-8',
                    'w-8',
                    'flex',
                    'items-center',
                    'justify-center',
                    isPlaceholder ? 'bg-neutral-30' : 'bg-brand-100'
                )}
            >
                <BPImage
                    src={`${process.env.PUBLIC_PREFIX}/svg/icons/intercom.svg`}
                    height={16}
                    width={14}
                    alt="Live chat"
                />
            </div>
        </Button>
    );
};

export const IntercomPlaceHolder = () => {
    return <IntercomButton isPlaceholder />;
};

export const IntercomButtonGeneric = ({
    initiateImmediately = false,
    ...buttonProps
}: { initiateImmediately?: boolean } & ComponentProps<typeof Button>) => {
    return <Button {...buttonProps} onClick={() => show()} />;
};

export const IntercomButtonCustomLauncher = ({
    initiateImmediately = false,
    ...buttonProps
}: { initiateImmediately?: boolean } & ComponentProps<typeof Button>) => {
    return <Button {...buttonProps} id="intercom-launcher" />;
};

export const IntercomWrapperGeneric = (
    { isCustomLauncher, ...buttonProps }: {
        initiateImmediately?: boolean;
        isCustomLauncher?: boolean;
    } & ComponentProps<typeof Button>
) => {
    const {
        concent: { required },
    } = useAppContext();
    const user = useLoggedUser();

    useEffect(() => {
        if (!window.Intercom) {
            initializeIntercom(user);
        }
    }, [user]);

    if (!required) {
        return 'IC';
    }

    if (isCustomLauncher) return <IntercomButtonCustomLauncher {...buttonProps} />;

    return <IntercomButtonGeneric {...buttonProps} />;
};
